import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import type { MetricRecord } from "@/metrics/runtime"

import { useOptionalMetricViewer } from "../../contexts/MetricViewerContext"

interface TestMetricRuntimeContextValue {
  metric: MetricRecord | null
  isNewMetric: boolean
  toleranceSeconds: number
  setToleranceSeconds: (value: number) => void
  includeOldVersions: boolean
  setIncludeOldVersions: (value: boolean) => void
  simulationIds?: string[]
  annotationSimulationIds?: string[]
  criteriaFirst?: boolean
  criteriaViewMode: "score" | "criteria"
  criteriaOverride?: string[]
  experimentStorageScope?: unknown
  beforeRun?: () => Promise<void>
  onRunningChange?: (isRunning: boolean) => void
}

const TestMetricRuntimeContext =
  createContext<TestMetricRuntimeContextValue | null>(null)

export function TestMetricRuntimeProvider({
  children,
  metric,
  simulationIds,
  annotationSimulationIds,
  criteriaFirst = false,
  criteriaViewMode = "score",
  criteriaOverride,
  experimentStorageScope,
  beforeRun,
  onRunningChange,
}: {
  children: ReactNode
  metric: MetricRecord | null
  simulationIds?: string[]
  annotationSimulationIds?: string[]
  criteriaFirst?: boolean
  criteriaViewMode?: "score" | "criteria"
  criteriaOverride?: string[]
  experimentStorageScope?: unknown
  beforeRun?: () => Promise<void>
  onRunningChange?: (isRunning: boolean) => void
}) {
  const [toleranceSeconds, setToleranceSeconds] = useState(1)
  const [includeOldVersions, setIncludeOldVersions] = useState(false)
  const value = useMemo<TestMetricRuntimeContextValue>(
    () => ({
      metric,
      isNewMetric: false,
      toleranceSeconds,
      setToleranceSeconds,
      includeOldVersions,
      setIncludeOldVersions,
      simulationIds,
      annotationSimulationIds,
      criteriaFirst,
      criteriaViewMode,
      criteriaOverride,
      experimentStorageScope,
      beforeRun,
      onRunningChange,
    }),
    [
      metric,
      toleranceSeconds,
      includeOldVersions,
      simulationIds,
      annotationSimulationIds,
      criteriaFirst,
      criteriaViewMode,
      criteriaOverride,
      experimentStorageScope,
      beforeRun,
      onRunningChange,
    ],
  )

  return (
    <TestMetricRuntimeContext.Provider value={value}>
      {children}
    </TestMetricRuntimeContext.Provider>
  )
}

export function useTestMetricRuntime(): TestMetricRuntimeContextValue {
  const runtime = useContext(TestMetricRuntimeContext)
  const metricViewer = useOptionalMetricViewer()
  if (runtime) {
    return runtime
  }
  if (metricViewer) {
    return metricViewer
  }
  throw new Error(
    "useTestMetricRuntime must be used within a test metric or metric viewer provider",
  )
}
